import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  InjectionToken,
  INJECTOR,
  input,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { inputFn, LoadingSpinnerComponent, ResolvableModal } from '@softline/ui-core';
import { Cart } from '@softapps/wws/pos-core';
import { TerminalService } from '@softapps/wws/pos-core';
import { block, handleRequestError } from '@softline/application';
import { isDefinedNotEmpty } from '@softline/core';
import {
  catchError,
  concatMap,
  defer,
  EMPTY,
  from,
  of,
  take,
  withLatestFrom,
} from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { TerminalInitService } from '@softapps/wws/pos-core';

export const TERMINAL_MODAL_INIT = new InjectionToken<
  (resolveFn: (value: boolean) => void) => Promise<void>
>('TERMINAL_MODAL_INIT');

export const TERMINAL_MODAL_SSE_KEY = new InjectionToken<string>(
  'TERMINAL_MODAL_SSE_KEY'
);

@Component({
  selector: 'soft-pos-terminal-modal',
  imports: [CommonModule, LoadingSpinnerComponent],
  templateUrl: './terminal-modal.component.html',
  styleUrl: './terminal-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TerminalService],
})
export class TerminalModalComponent extends ResolvableModal<boolean> implements OnInit {
  readonly terminalService = inject(TerminalService);
  readonly injector = inject(INJECTOR);
  readonly _ = inject(TerminalInitService);
  readonly initFn = inject(TERMINAL_MODAL_INIT, { optional: true });

  readonly finalizeRequired = input(false);
  readonly cart = input<Cart | undefined>(undefined);

  readonly input = inputFn();
  readonly messages = computed(() => this.terminalService.messages());

  readonly finalizeRequired$ = toObservable(this.finalizeRequired);
  readonly showManualDismissButton = signal(false);

  constructor() {
    super();

    // React to different states
    this.terminalService.finalizeReceived$
      .pipe(
        take(1),
        withLatestFrom(this.finalizeRequired$),
        concatMap(([msg, required]) => {
          if (required && msg.type !== 'terminal/payment/finalize') return of(null);

          return defer(() =>
            from(this.finalize(msg?.data?.terminalPaymentId)).pipe(
              catchError((e) => {
                handleRequestError(e, this.injector, { show: true });
                return EMPTY;
              })
            )
          );
        })
      )
      .subscribe();

    effect(async () => {
      const state = this.terminalService.state();

      await untracked(async () => {
        if (state === 'authorization') {
          await this.startAuthorization();
        } else if (state === 'completedFailure') {
          this.showManualDismissButton.set(true);
        }
      });
    });
  }

  async ngOnInit() {
    if (this?.initFn) {
      await this.initFn((value) => this.resolve(value));
    }
  }

  async finalize(terminalPaymentId?: number): Promise<void> {
    const cart = this.cart();

    if (!terminalPaymentId || !cart?.id) {
      this.resolve(true);
      return;
    }

    await block(async () => {
      try {
        if (!cart?.id) {
          this.resolve(false);
          return Promise.resolve();
        }

        await this.terminalService.finalize(cart?.id, terminalPaymentId);
        this.resolve(true);
      } catch (e) {
        this.resolve(false);
        handleRequestError(e, this.injector, { show: true });
      }
    }, this.injector);
  }

  async startAuthorization(): Promise<void> {
    const cart = this.cart();
    if (!cart?.id) return;

    try {
      const result = await this.input({
        icon: 'fa-regular fa-credit-card-front',
        title: 'Authorisierung notwendig',
        position: 'keyboard-start',
        subtitle: 'Um fortzufahren ist ein Code notwendig',
        placeholder: 'Code eingeben',
        dismiss: false,
        textValue: '',
        multiline: false,
      });

      if (!isDefinedNotEmpty(result) || result === 'DISMISSED') return;

      await block(async () => {
        if (!cart?.id) return Promise.resolve();

        try {
          await this.terminalService.authorize(cart.id, result);
        } catch (e) {
          handleRequestError(e, this.injector, { show: true });
        }
      }, this.injector);
    } catch (e) {
      this.resolve(false);
      handleRequestError(e, this.injector, { show: true });
    }
  }
}
